// R1-ROADS (WS-62): fences that follow a road. Pure (no three.js, no DOM): main.js draws the result, and the
// package check (scripts/ci/check-package.mjs) runs the same code to prove that no fence stands on a road.
//
// resort.fences[].lines[] item, besides a plain [[x, z], ...] line:
//   { road: "<road name>" | [x, z], side: "left" | "right", offset: m, from: [x, z], to: [x, z], clear: m }
// - road: the drawn road (scene.json roads, plus drawn roads) by its name, or the road nearest a point;
// - from / to (optional): where the fence starts and ends along that road (projected onto it); the side is the
//   hand of someone walking from `from` to `to` (or along the road's own direction), seen from above with north up;
// - offset: metres from the road centreline (default: half the road width + 1.5 m, on the plough bank);
// - clear: the fence stops `clear` m (default 0.8) short of the edge of any other road it would cross, so there is a
//   gap where another road joins on its side: a fence never stands on a road.
// VIS-169: { path: "<path name>", ... } follows a drawn path instead (scene.json paths: a sleigh or husky track fenced
// on both sides); such a fence stops short of every other road AND path it would cross (a gap where a track crosses a
// forest road drawn as a path), and where its own path joins itself (a T or a lane used both ways) the self check
// below leaves the opening. The other pieces of a path of the same name (a track traced in pieces that meet at forks)
// are the path itself, not ways it crosses: its fence goes on until it would stand in their lane, so the fences of the
// pieces meet at a fork and join (was: 3.6 m short of every other piece, a gap each side of each fork, owner
// 2026-10-29). A fence round the inside of a loop whose two ends meet at its tip (within LOOP_TIP) is closed there.

import { resample, nearestOn, nearAny } from './lines/measure.js'; // VIS-197: the shared sums of every line

const dist = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1]);
/** m: a path fence whose two ends are this close (the tip of a loop's inside, where its lanes meet) is closed there. */
const LOOP_TIP = 2.5;
/** The name a path's pieces share (lazy/ways.js renames a reshaped way it follows and keeps its name as `family`). */
const familyOf = (r) => r?.family ?? r?.name ?? null;

/** The road (or path) a fence line names: by name (the one nearest `hint` when several have it) or nearest a point. WS-80: exported (lazy/ways.js). */
export function pickRoad(roads, ref, hint) {
  if (typeof ref === 'string') {
    const named = roads.filter((r) => r.name === ref && r.pts?.length > 1);
    if (!named.length) return null;
    if (named.length === 1 || !hint) return named.sort((a, b) => b.pts.length - a.pts.length)[0];
    return named.reduce((m, r) => (nearestOn(r.pts, hint).d < nearestOn(m.pts, hint).d ? r : m));
  }
  if (Array.isArray(ref)) {
    let best = null, bd = Infinity;
    for (const r of roads) { if (!(r.pts?.length > 1)) continue; const d = nearestOn(r.pts, ref).d; if (d < bd) { bd = d; best = r; } }
    return best;
  }
  return null;
}

/**
 * Lines of one resort.fences entry -> plain point lines. `roads` = [{ name, kind, pts, width? }]; `halfOf(road)` = the
 * drawn half width. A spec whose road is missing yields nothing (and is reported in `problems`). `paths` = the drawn
 * paths (VIS-169: lines with `path` follow one of them).
 */
export function resolveFenceLines(lines, roads, halfOf, problems = [], paths = []) {
  const out = [], grids = new Map(); // WS-88: the other ways by cell (nearAny), made once per call, per clearance and per path name
  const kin = (r, fam) => fam != null && familyOf(r) === fam; // (a piece of the same path)
  const gridOf = (onPath, clear, fam) => { const k = `${onPath}|${clear}|${fam}`; if (!grids.has(k)) grids.set(k, nearAny((onPath ? [...roads, ...paths.filter((r) => !kin(r, fam))] : roads).filter((r) => r.pts?.length > 1), (r) => halfOf(r) + clear)); return grids.get(k); };
  for (const spec of lines || []) {
    if (Array.isArray(spec)) { if (spec.length > 1) out.push(spec); continue; }
    const onPath = spec?.path != null;
    if (spec?.road == null && !onPath) continue;
    const road = onPath ? pickRoad(paths, spec.path, spec.from || spec.to) : pickRoad(roads, spec.road, spec.from || spec.to);
    if (!road) { problems.push(`fence along ${onPath ? 'path' : 'road'} ${JSON.stringify(onPath ? spec.path : spec.road)}: no such ${onPath ? 'path' : 'road'}`); continue; }
    let pts = resample(road.pts, 1);
    const cum = [0]; for (let i = 1; i < pts.length; i++) cum.push(cum[i - 1] + dist(pts[i], pts[i - 1]));
    let s0 = spec.from ? nearestOn(pts, spec.from).s : 0, s1 = spec.to ? nearestOn(pts, spec.to).s : cum[cum.length - 1];
    const rev = s1 < s0;
    if (rev) [s0, s1] = [s1, s0];
    pts = pts.filter((_, i) => cum[i] >= s0 - 1e-6 && cum[i] <= s1 + 1e-6);
    if (rev) pts.reverse();
    if (pts.length < 2) continue;
    const half = halfOf(road), off = spec.offset ?? half + 1.5, sign = spec.side === 'left' ? 1 : -1;
    const clear = spec.clear ?? 0.8;
    const fam = onPath ? familyOf(road) : null;
    const onOtherAt = gridOf(onPath, clear, fam); // VIS-169: a path's fence opens at every way it crosses (WS-88: all of them but its own, by cell)
    const onSelfAt = nearAny(onPath ? [road, ...paths.filter((r) => r !== road && r.pts?.length > 1 && kin(r, fam))] : [road], () => off - 0.25); // (+ the other pieces of its path)
    const W = 3; // tangent window (points = metres): smooth normals, no kinks at vertices
    let run = [];
    const runLen = (r) => r.slice(1).reduce((s, p, k) => s + dist(p, r[k]), 0); // VIS-169: by length, so a fence round a loop (ends meet) is kept
    const flush = () => {
      if (run.length > 1 && runLen(run) >= 2) {
        const gap = dist(run[0], run.at(-1));
        if (onPath && gap > 1e-6 && gap <= LOOP_TIP && runLen(run) > 10 * LOOP_TIP) run.push(run[0]); // (the inside of a loop, closed at its tip)
        if (onPath) run.onPath = true; // (joinRuns: joined within LOOP_TIP)
        out.push(run);
      }
      run = [];
    };
    for (let i = 0; i < pts.length; i++) {
      const a = pts[Math.max(0, i - W)], b = pts[Math.min(pts.length - 1, i + W)];
      let tx = b[0] - a[0], tz = b[1] - a[1]; const L = Math.hypot(tx, tz) || 1; tx /= L; tz /= L;
      // left of the direction of travel, seen from above with north up (x east, z south) = (tz, -tx)
      const p = [pts[i][0] + sign * tz * off, pts[i][1] - sign * tx * off];
      const onSelf = onSelfAt(p); // folded back on the inside of a tight bend (nearestOn(road.pts, p).d < off - 0.25)
      const onOther = onOtherAt(p, road); // some other way r with nearestOn(r.pts, p).d < halfOf(r) + clear
      if (onSelf || onOther) { flush(); continue; }
      run.push([Math.round(p[0] * 100) / 100, Math.round(p[1] * 100) / 100]);
    }
    flush();
  }
  return joinRuns(out);
}

/** Where segments p1-p2 and q1-q2 cross, or null. */
function cross(p1, p2, q1, q2) {
  const rx = p2[0] - p1[0], rz = p2[1] - p1[1], sx = q2[0] - q1[0], sz = q2[1] - q1[1], d = rx * sz - rz * sx;
  if (Math.abs(d) < 1e-9) return null;
  const t = ((q1[0] - p1[0]) * sz - (q1[1] - p1[1]) * sx) / d, u = ((q1[0] - p1[0]) * rz - (q1[1] - p1[1]) * rx) / d;
  return t >= 0 && t <= 1 && u >= 0 && u <= 1 ? [Math.round((p1[0] + t * rx) * 100) / 100, Math.round((p1[1] + t * rz) * 100) / 100] : null;
}
/** Run `a` then run `b`; where they overlap (the end of `a` and the start of `b` cross within `reach` m: the fences of
 *  two pieces on the inside of a fork) both are cut back to the crossing, so the joined fence has no zig-zag. */
function glue(a, b, reach = 3) {
  for (let i = a.length - 1, la = 0; i > 0 && la <= reach; la += dist(a[i], a[i - 1]), i--)
    for (let j = 0, lb = 0; j + 1 < b.length && lb <= reach; lb += dist(b[j], b[j + 1]), j++) {
      const X = cross(a[i - 1], a[i], b[j], b[j + 1]);
      if (X) return [...a.slice(0, i), X, ...b.slice(j + 1)];
    }
  return [...a, ...b];
}
/**
 * Runs whose ends meet (within `tol` m) become one line, so a fence that goes on from one road to the next (round a
 * corner, along a joining lane) has no break. A gap left for a road is wider (the road plus `clear` on both sides).
 * Two runs along paths (`onPath`, resolveFenceLines) join within LOOP_TIP: round the outside of a fork their ends
 * stand up to ~2 m apart, and the narrowest gap a path fence leaves for a way it crosses is wider.
 */
export function joinRuns(runs, tol = 1.5) {
  const tag = (r, on) => { if (on) r.onPath = true; return r; };
  const out = runs.map((r) => tag(r.slice(), r.onPath));
  for (let i = 0; i < out.length; i++) for (let j = 0; j < out.length; j++) {
    const a = out[i], b = out[j], e = a.at(-1), t = a.onPath && b.onPath ? Math.max(tol, LOOP_TIP) : tol;
    const c = i != j && (dist(e, b[0]) <= t ? glue(a, b) : dist(e, b.at(-1)) <= t ? glue(a, b.reverse()) : i < j && dist(a[0], b[0]) <= t && glue(a.reverse(), b));
    if (c) { out[i] = tag(c.filter((p, k) => !k || dist(p, c[k - 1])), a.onPath && b.onPath); out.splice(j, 1); i = -1; break; } // start again
  }
  return out;
}

/**
 * A paddock ring minus the edges that run along other fence lines (both ends within `tol` m of one of `others`): the
 * runs left, as open lines, so a paddock side that is another fence (a lit fence along the road) is not drawn twice.
 * The ring itself when no edge is shared; [] when every edge is.
 */
export function ringBeside(ring, others, tol = 1) {
  const lines = (others || []).filter((l) => l?.length > 1), n = ring.length;
  const near = ring.map((p) => lines.some((l) => nearestOn(l, p).d <= tol));
  const keep = ring.map((_, i) => !(near[i] && near[(i + 1) % n])); // edge i = ring[i] -> ring[i + 1]
  if (keep.every(Boolean)) return ring;
  if (!keep.some(Boolean)) return [];
  const start = keep.findIndex((k, i) => k && !keep[(i - 1 + n) % n]); // a kept edge right after a shared one
  const out = [];
  let cur = null;
  for (let j = 0; j < n; j++) {
    const i = (start + j) % n;
    if (!keep[i]) { cur = null; continue; }
    if (!cur) out.push((cur = [ring[i]]));
    cur.push(ring[(i + 1) % n]);
  }
  return out;
}
